import React, { useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import BasePlot from "./BasePlot";

const Plot = createPlotlyComponent(Plotly);

export interface HistogramPlotProps {
  data: any[];
  metric?: string;
  title?: string;
  defaultColor?: string;
  defaultBins?: number;
  className?: string;
  plotRef?: string;
  isFullscreen?: boolean;
}

const HistogramPlot = ({
  data,
  metric = "total_counts",
  title = "Histogram Plot",
  defaultColor = "#636EFA",
  defaultBins = 50,
  className = "",
  plotRef,
  isFullscreen = false,
}: HistogramPlotProps): React.ReactElement => {
  const [color, setColor] = useState(defaultColor);
  const [numBins, setNumBins] = useState(defaultBins);

  const xData = data.map((d) => d[metric]);

  const plotComponent = (
    <Plot
      data={[
        {
          type: "histogram",
          x: xData,
          nbinsx: numBins,
          marker: { color },
          opacity: 0.75,
          name: `${metric} distribution`,
        },
      ]}
      layout={{
        title: { text: `Distribution of ${metric}`, font: { size: 14 } },
        // Let the square plot card determine the height. A fixed height here
        // left unused space below histograms in the normalization grid.
        height: undefined,
        autosize: true,
        paper_bgcolor: "white",
        plot_bgcolor: "white",
        margin: { l: 60, r: 30, t: 40, b: 50 }, // Adequate margins to prevent label cutoff
        xaxis: { title: metric },
        yaxis: { title: "Count" },
        bargap: 0.1,
      }}
      config={{
        displayModeBar: false,
        responsive: true,
      }}
      style={{ width: "100%", height: "100%" }}
      className={plotRef ? plotRef.replace("#", "") : "plotly-plot"}
    />
  );

  return (
    <BasePlot
      title={title}
      className={className}
      showColorPicker={true}
      defaultColor={defaultColor}
      onColorChange={setColor}
      showSizeSlider={true}
      defaultSize={defaultBins}
      onSizeChange={setNumBins}
      downloadFilename={`${metric}_histogram`}
      plotType="histogram"
      plotProps={{
        data,
        metric,
        defaultBins,
      }}
      isFullscreen={isFullscreen}
    >
      {plotComponent}
    </BasePlot>
  );
};

export default HistogramPlot;
